import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSort, faEllipsisVertical, faCopy, faCheck, faRotateRight, faQrcode, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import SortModal from './SortModal';
import styles from './ConnectionTable.module.css';
import NovaConexao from './NovaConexao/novaConect';
import InstanceConfigModal from './NovaConexao/Qrcode/SuccessQR/Instance';
import ConnectQrModal from './ConnectQrModal';
import useAuth from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getInstanceStatus, updateInstancePhoneNumber } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../config/api';

const ConnectionTable = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [connections, setConnections] = useState([]);
  const [originalConnections, setOriginalConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [novaConexaoOpen, setNovaConexaoOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [currentSort, setCurrentSort] = useState(null);
  const [selectedSort, setSelectedSort] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInstance, setShowInstance] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectInstance, setConnectInstance] = useState(null);
  const [copied, setCopied] = useState({});
  const [instanceStatuses, setInstanceStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [instanceConfigOpen, setInstanceConfigOpen] = useState(false);
  const [selectedInstanceConfig, setSelectedInstanceConfig] = useState(null);
  const [initialStatusLoaded, setInitialStatusLoaded] = useState(false);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const fetchUserInstances = async () => {
    if (!user?.id) {
      setConnections([]);
      setOriginalConnections([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Primeiro, busca os dados do usuário para verificar se é Enterprise
      const userData = await apiRequest(`/auth/user/${user.id}`);

      let instancesData;

      if (userData?.enterprise_id) {
        // Se é Enterprise, busca instâncias da enterprise
        instancesData = await apiRequest(`/enterprises/${userData.enterprise_id}/instances`);
      } else {
        // Busca instâncias do usuário
        instancesData = await apiRequest(`/instances/user/${user.id}`);
      }

      // Backend retorna { success: true, instances: [...] }
      const instances = instancesData?.instances || instancesData || [];

      const formattedConnections = (Array.isArray(instances) ? instances : []).map(instance => ({
        id: instance.id,
        name: instance.name || 'Instância sem nome',
        number: instance.phone_number || instance.number || 'Não vinculado',
        instance: instance.instanceName || instance.instancename,
        instanceName: instance.instanceName || instance.instancename,
        apiKey: instance.apikey,
        status: mapStatus(instance.status),
        rawData: instance,
        server_id: instance.server_id,
        phone_number: instance.phone_number,
        host: instance.server_host || instance.Servers?.host || instance.host || 'Host não encontrado'
      }));

      setConnections(formattedConnections);
      setOriginalConnections(formattedConnections);
    } catch (err) {
      console.error('Erro ao buscar instâncias:', err);
      setError(err.message);
      setConnections([]);
      setOriginalConnections([]);
    } finally {
      setLoading(false);
    }
  };

  const mapStatus = (status) => {
    if (!status) return 'disconnected';
    
    const statusMap = {
      'connected': 'online',
      'open': 'online',
      'connecting': 'connecting',
      'disconnected': 'disconnected',
      'close': 'disconnected',
      'error': 'error',
      'fail': 'error'
    };
    
    return statusMap[status.toLowerCase()] || 'disconnected';
  };

  const fetchAllInstanceStatuses = async (connectionsList) => {
    setStatusLoading(true);
    const statuses = {};
    let hasPhoneNumberUpdate = false;

    await Promise.all(
      connectionsList.map(async (conn) => {
        try {
          const statusRes = await getInstanceStatus(
            conn.instanceName || conn.instance,
            conn.server_id
          );
          statuses[conn.id] = statusRes?.status || statusRes?.instanceStatus || 'UNKNOWN';
          
          // Se a instância está conectada e não tem número de telefone, atualiza
          if (statusRes?.status === 'WORKING' && statusRes?.me?.id && !conn.phone_number) {
            // Extrai apenas o número do telefone, removendo @c.us
            const phoneNumber = statusRes.me.id.replace('@c.us', '');
            await updateInstancePhoneNumber(conn.id, phoneNumber);
            hasPhoneNumberUpdate = true;
          }
        } catch {
          statuses[conn.id] = 'UNKNOWN';
        }
      })
    );
    
    setInstanceStatuses(prev => ({ ...prev, ...statuses }));
    setStatusLoading(false);
    
    // Se houve atualização de número de telefone, recarrega as instâncias
    if (hasPhoneNumberUpdate) {
      fetchUserInstances();
    }
  };

  useEffect(() => {
    fetchUserInstances();
  }, [user?.id]);

  // Busca status apenas das instâncias atualmente paginadas/visíveis
  useEffect(() => {
    if (originalConnections.length === 0) return;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    // Recalcula o filtro localmente para evitar TDZ ao referenciar variáveis declaradas abaixo
    const localFiltered = connections.filter(connection => {
      if (!searchTerm.trim()) return true;
      return (
        connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connection.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connection.instanceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connection.host.toLowerCase().includes(searchTerm.toLowerCase()) ||
        connection.apiKey.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    const visibleConnections = localFiltered.slice(startIndex, endIndex);
    fetchAllInstanceStatuses(visibleConnections).then(() => setInitialStatusLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalConnections, connections, searchTerm, currentPage, itemsPerPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getStatusStyles = (status, instanceId) => {
    const realStatus = instanceStatuses[instanceId];
    
    // Verifica status real da instância
    if (typeof realStatus !== 'undefined') {
      const statusUpper = String(realStatus).toUpperCase();
      
      if (statusUpper === 'WORKING') {
        return { color: '#10b981', text: t('connectionTable.status.connected') };
      }
      
      if (statusUpper === 'SCAN_QR_CODE') {
        return { color: '#f59e0b', text: t('connectionTable.status.waitingConnection') };
      }
      
      // Qualquer outro status
      return { color: '#ef4444', text: t('connectionTable.status.disconnected') };
    }
    
    // Fallback para status mapeados localmente
    const statusMap = {
      online: { color: '#10b981', text: t('connectionTable.status.connected') },
      connecting: { color: '#f59e0b', text: t('connectionTable.status.waitingConnection') },
      error: { color: '#ef4444', text: t('connectionTable.status.error') },
      disconnected: { color: '#ef4444', text: t('connectionTable.status.disconnected') },
    };
    return statusMap[status] || { color: '#9ca3af', text: t('connectionTable.status.unknown') };
  };

  const sortConnections = (sortType) => {
    let sorted = [...originalConnections];
    
    const sortFunctions = {
      'name-asc': (a, b) => a.name.localeCompare(b.name),
      'name-desc': (a, b) => b.name.localeCompare(a.name),
      'status-online': (a, b) => {
        const aStatus = instanceStatuses[a.id];
        const bStatus = instanceStatuses[b.id];
        const aIsOnline = aStatus && String(aStatus).toUpperCase() === 'WORKING';
        const bIsOnline = bStatus && String(bStatus).toUpperCase() === 'WORKING';
        if (aIsOnline && !bIsOnline) return -1;
        if (!aIsOnline && bIsOnline) return 1;
        return 0;
      },
      'status-offline': (a, b) => {
        const aStatus = instanceStatuses[a.id];
        const bStatus = instanceStatuses[b.id];
        const aIsOnline = aStatus && String(aStatus).toUpperCase() === 'WORKING';
        const bIsOnline = bStatus && String(bStatus).toUpperCase() === 'WORKING';
        if (!aIsOnline && bIsOnline) return -1;
        if (aIsOnline && !bIsOnline) return 1;
        return 0;
      },
      'recent-first': (a, b) => new Date(b.rawData.created_at) - new Date(a.rawData.created_at),
      'oldest-first': (a, b) => new Date(a.rawData.created_at) - new Date(b.rawData.created_at),
    };

    if (sortFunctions[sortType]) {
      sorted.sort(sortFunctions[sortType]);
    }

    setConnections(sorted);
    setCurrentSort(sortType);
  };

  const applySort = () => {
    if (selectedSort) {
      sortConnections(selectedSort);
    }
    setSortModalOpen(false);
  };

  const resetSorting = () => {
    setConnections(originalConnections);
    setCurrentSort(null);
    setSelectedSort(null);
  };

  const handleConfigClick = async (connection) => {
    setSelectedInstanceConfig(connection);
    setInstanceConfigOpen(true);
  };

  const truncate = (text, max = 24) => {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '...' : text;
  };

  const handleNovaConexaoClose = () => {
    setNovaConexaoOpen(false);
    setInitialStatusLoaded(false); // Reseta o status de carregamento inicial
    fetchUserInstances();
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
  };

  const handleOpenConnectModal = (connection) => {
    setConnectInstance(connection);
    setConnectModalOpen(true);
  };

  const handleCloseConnectModal = () => {
    setConnectModalOpen(false);
    setConnectInstance(null);
  };

  const isInstanceConnected = (connection) => {
    const realStatus = instanceStatuses[connection.id];
    return realStatus && String(realStatus).toUpperCase() === 'WORKING';
  };

    const filteredConnections = connections.filter(connection => {
    if (!searchTerm.trim()) {
      return true; // Show all connections if no search term
    }
    
    return (
      connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.instanceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.host.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.apiKey.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Calcular conexões paginadas
  const totalConnections = filteredConnections.length;
  const totalPages = Math.ceil(totalConnections / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedConnections = filteredConnections.slice(startIndex, endIndex);



  if (loading || (originalConnections.length > 0 && !initialStatusLoaded)) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#00FF99' }}>
          {loading ? 'Carregando instâncias...' : 'Carregando status das instâncias...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
          Erro ao carregar instâncias: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.actionBar}>
        <div className={styles.filterButtons}>
          <div className={styles.searchContainer}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <input
              type="text"
              placeholder={t('connectionTable.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button
                className={styles.clearSearchButton}
                onClick={() => setSearchTerm('')}
                title={t('connectionTable.clearSearch')}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <button
            className={`${styles.actionButton} ${currentSort ? styles.activeSort : ''}`}
            onClick={() => setSortModalOpen(true)}
            title={currentSort ? t('connectionTable.sortActive') : t('connectionTable.sort')}
          >
            <FontAwesomeIcon icon={faSort} />
            {currentSort ? t('connectionTable.sortActive') : t('connectionTable.sort')}
          </button>
          <button
            className={styles.actionButton}
            onClick={() => {
              fetchUserInstances();
              setInitialStatusLoaded(false); // Reseta o status de carregamento inicial
            }}
            disabled={statusLoading}
          >
            <FontAwesomeIcon icon={faRotateRight} spin={statusLoading} />
            {t('connectionTable.refresh')}
          </button>
        </div>
        <button
          className={styles.novaConexaoButton}
          onClick={() => setNovaConexaoOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t('connectionTable.newConnection')}
        </button>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('connectionTable.name')}</th>
              <th>{t('connectionTable.phone')}</th>
              <th>{t('connectionTable.instanceName')}</th>
              <th>{t('connectionTable.host')}</th>
              <th>{t('connectionTable.apiKey')}</th>
              <th>{t('connectionTable.statusTitle')}</th>
              <th>{t('connectionTable.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedConnections.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                  {totalConnections === 0 ? t('connectionTable.noConnections') : t('connectionTable.pagination.noConnectionsOnPage')}
                </td>
              </tr>
            ) : (
              paginatedConnections.map((connection) => {
                const statusStyles = getStatusStyles(connection.status, connection.id);
                return (
                  <tr key={connection.id}>
                    <td>
                      {connection.name.length > 10 ? (
                        <div className={styles.apiKey}>
                          <span>{connection.name.substring(0, 10)}...</span>
                          <button
                            className={styles.showApiKey}
                            onClick={() => copyToClipboard(connection.name, `name-${connection.id}`)}
                          >
                            <FontAwesomeIcon
                              icon={copied[`name-${connection.id}`] ? faCheck : faCopy}
                              style={{ color: copied[`name-${connection.id}`] ? '#00FF99' : undefined }}
                            />
                          </button>
                        </div>
                      ) : (
                        connection.name
                      )}
                    </td>
                    <td>{truncate(connection.number)}</td>
                    <td>{truncate(connection.instanceName)}</td>
                    <td>
                      <div className={styles.apiKey}>
                        <span>{truncate(connection.host, 20)}</span>
                        <button
                          className={styles.showApiKey}
                          onClick={() => copyToClipboard(connection.host, `host-${connection.id}`)}
                        >
                          <FontAwesomeIcon
                            icon={copied[`host-${connection.id}`] ? faCheck : faCopy}
                            style={{ color: copied[`host-${connection.id}`] ? '#00FF99' : undefined }}
                          />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className={styles.apiKey}>
                        <span>{truncate(connection.apiKey, 20)}</span>
                        <button
                          className={styles.showApiKey}
                          onClick={() => copyToClipboard(connection.apiKey, connection.id)}
                        >
                          <FontAwesomeIcon
                            icon={copied[connection.id] ? faCheck : faCopy}
                            style={{ color: copied[connection.id] ? '#00FF99' : undefined }}
                          />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className={styles.statusCell}>
                        <div
                          className={styles.statusDot}
                          style={{ backgroundColor: statusStyles.color }}
                        />
                        {statusStyles.text}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        { !isInstanceConnected(connection) && (
                          <button
                            className={styles.actionsButton}
                            onClick={() => handleOpenConnectModal(connection)}
                            title={t('connectionTable.actionsConfig.connect')}
                          >
                            <FontAwesomeIcon icon={faQrcode} />
                          </button>
                        )}
                        <button
                          className={styles.actionsButton}
                          onClick={() => handleConfigClick(connection)}
                          title={t('connectionTable.actionsConfig.config')}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Informações de paginação e controles */}
      {totalConnections > 0 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            <span>
              {t('connectionTable.pagination.showing', {
                start: totalConnections > 0 ? startIndex + 1 : 0,
                end: Math.min(endIndex, totalConnections),
                total: totalConnections
              })}
            </span>
          </div>
          
          {totalPages > 1 && (
            <div className={styles.paginationControls}>
              <select 
                value={itemsPerPage} 
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={styles.itemsPerPageSelect}
              >
                <option value={10}>10 {t('connectionTable.pagination.perPage')}</option>
                <option value={25}>25 {t('connectionTable.pagination.perPage')}</option>
                <option value={50}>50 {t('connectionTable.pagination.perPage')}</option>
              </select>
              
              <div className={styles.paginationButtons}>
                <button
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t('connectionTable.pagination.previous')}
                </button>
                
                <div className={styles.paginationNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`${styles.paginationNumber} ${currentPage === pageNum ? styles.active : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t('connectionTable.pagination.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <SortModal
        isOpen={sortModalOpen}
        selectedSort={selectedSort}
        setSelectedSort={setSelectedSort}
        applySort={applySort}
        resetSorting={resetSorting}
        onClose={() => setSortModalOpen(false)}
      />

      <NovaConexao
        isOpen={novaConexaoOpen}
        onClose={handleNovaConexaoClose}
      />

      {selectedInstanceConfig && (
        <InstanceConfigModal
          isOpen={instanceConfigOpen}
          onClose={() => {
            setInstanceConfigOpen(false);
            setSelectedInstanceConfig(null);
          }}
          connection={selectedInstanceConfig}
          instanceName={selectedInstanceConfig?.instanceName || selectedInstanceConfig?.instance}
          isConnected={isInstanceConnected(selectedInstanceConfig)}
        />
      )}

      {connectInstance && (
        <ConnectQrModal
          isOpen={connectModalOpen}
          onClose={handleCloseConnectModal}
          connection={connectInstance}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default ConnectionTable;